import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Database, ExternalLink, Server, Globe, AlertCircle, RefreshCw, Mail, Clock, FileText, Shield } from 'lucide-react';
import { LeaderboardAd, InContentAd, RectangleAd } from '@/components/shared/AdSense';
import AdFrame from '@/components/shared/AdFrame';

const DataSources = () => {
  return (
    <>
      <Helmet>
        <title>Data Sources - Wage Atlas | Salary Data Sources & Attribution</title>
        <meta name="description" content="Comprehensive documentation of Wage Atlas data sources including U.S. Bureau of Labor Statistics (BLS) OEWS data, BEA Regional Price Parities, and data processing infrastructure. Transparent attribution and data coverage information." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://wageatlas.com/data-sources/" />
        <meta name="keywords" content="BLS data, OEWS, salary data sources, Bureau of Labor Statistics, BEA RPP, wage data attribution, government salary data" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-4 gap-8">
              <div className="lg:col-span-3 space-y-8">
                {/* Header */}
                <div className="text-center space-y-4">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Database className="h-8 w-8 text-primary" />
                    <h1 className="text-3xl font-bold">Data Sources</h1>
                  </div>
                  <p className="text-lg text-muted-foreground">
                    Transparent documentation of all data sources, APIs, and services powering Wage Atlas salary intelligence.
                  </p>
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>Last updated: June 2026</span>
                  </div>
                </div>

                {/* Leaderboard Ad */}
                <AdFrame className="mb-8">
                  <LeaderboardAd />
                </AdFrame>

                {/* Primary Data Sources */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Database className="h-5 w-5 text-primary" />
                      Primary Data Sources
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* BLS Section */}
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        U.S. Bureau of Labor Statistics (BLS)
                        <a 
                          href="https://www.bls.gov" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                          aria-label="Visit BLS.gov (opens in new tab)"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="p-4 bg-muted/30 rounded-lg">
                          <h4 className="font-medium mb-2">Occupational Employment & Wage Statistics (OEWS)</h4>
                          <p className="text-sm text-muted-foreground mb-2">
                            Annual surveys covering approximately 800 occupations across all U.S. states and metropolitan areas. 
                            Provides mean and median wages, employment levels, and percentile distributions (10th, 25th, 75th, 90th).
                          </p>
                          <div className="text-xs text-muted-foreground space-y-1 mt-2">
                            <p><strong>Data Years:</strong> 1997-2025</p>
                            <p><strong>Update Frequency:</strong> Annual</p>
                            <p><strong>Coverage:</strong> National, State, and MSA levels</p>
                          </div>
                          <a 
                            href="https://www.bls.gov/oes/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline mt-2 inline-flex items-center gap-1"
                          >
                            Learn more at BLS.gov <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                        <div className="p-4 bg-muted/30 rounded-lg">
                          <h4 className="font-medium mb-2">
                            <Link to="/what-is-soc-code" className="hover:text-primary transition-colors">
                              Standard Occupational Classification (SOC)
                            </Link>
                          </h4>
                          <p className="text-sm text-muted-foreground mb-2">
                            Federal classification system organizing occupations into hierarchical categories. 
                            We use SOC 2018 codes for consistent occupation identification across all data years.
                          </p>
                          <div className="text-xs text-muted-foreground space-y-1 mt-2">
                            <p><strong>Version:</strong> SOC 2018</p>
                            <p><strong>Bridge:</strong> SOC 2010 → SOC 2018 mapping</p>
                          </div>
                          <Link to="/what-is-soc-code" className="text-xs text-primary hover:underline mt-2 inline-flex items-center gap-1">
                            Learn more about SOC codes <ExternalLink className="h-3 w-3" />
                          </Link>
                        </div>
                      </div>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-medium mb-2">BLS Education & Training Data</h4>
                        <p className="text-sm text-muted-foreground">
                          Required education levels and training pathways for occupations. Used to identify 
                          no-degree-required positions and entry-level opportunities.
                        </p>
                      </div>
                    </div>

                    {/* BEA Section */}
                    <div>
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        Bureau of Economic Analysis (BEA)
                        <a 
                          href="https://www.bea.gov" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                          aria-label="Visit BEA.gov (opens in new tab)"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </h3>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-medium mb-2">Regional Price Parities (RPP)</h4>
                        <p className="text-sm text-muted-foreground mb-2">
                          Cost-of-living adjustments by metropolitan area and state. Used to calculate "real wages" 
                          that account for purchasing power differences across locations. Available for Pro subscribers.
                        </p>
                        <div className="text-xs text-muted-foreground space-y-1 mt-2">
                          <p><strong>Coverage:</strong> States and select Metropolitan Statistical Areas (MSAs)</p>
                          <p><strong>Update Frequency:</strong> Annual</p>
                          <p><strong>Use Case:</strong> Cost-of-living adjusted salary comparisons</p>
                        </div>
                        <a 
                          href="https://www.bea.gov/data/prices-inflation/regional-price-parities-state-and-metro-area" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline mt-2 inline-flex items-center gap-1"
                        >
                          Learn more at BEA.gov <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </div>

                    {/* Additional Sources */}
                    <div>
                      <h3 className="font-semibold mb-3">Additional Government Sources</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            <strong>Industry Classifications:</strong> NAICS (North American Industry Classification System) codes for industry-specific analysis. We use NAICS 2022 with bridges from legacy NAICS 2017 and SIC codes.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            <strong>Economic Indicators:</strong> Unemployment rates, GDP growth, and labor force statistics for economic context in trends analysis.
                          </span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* In-Content Ad */}
                <InContentAd />

                {/* Data Processing & Infrastructure */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Server className="h-5 w-5 text-primary" />
                      Data Processing & Infrastructure
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h3 className="font-semibold mb-3">Data Transformation Pipeline</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-muted/30 rounded-lg">
                          <h4 className="font-medium mb-2">dbt (Data Build Tool)</h4>
                          <p className="text-sm text-muted-foreground">
                            We use dbt to transform raw BLS data into clean, structured fact and aggregate tables. 
                            This includes data quality checks, suppression handling, and bridge tables for classification systems.
                          </p>
                        </div>
                        <div className="p-4 bg-muted/30 rounded-lg">
                          <h4 className="font-medium mb-2">Supabase Postgres</h4>
                          <p className="text-sm text-muted-foreground">
                            Our primary database stores processed OEWS data, aggregates, and user data. 
                            Optimized with indexes and year-partitioned tables for fast queries.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-3">Data Quality & Validation</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            <strong>Suppression Handling:</strong> Properly identifies and handles BLS suppression indicators (#, *, **, -) in the data
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            <strong>Classification Bridges:</strong> Maps historical SOC 2010 codes to SOC 2018, and legacy NAICS/SIC codes to NAICS 2022
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            <strong>Data Validation:</strong> Automated checks for data consistency, completeness, and accuracy
                          </span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* Third-Party Services */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-5 w-5 text-primary" />
                      Third-Party Services
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground mb-4">
                      We use trusted third-party services to support platform functionality:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-medium mb-2">Google AdSense</h4>
                        <p className="text-sm text-muted-foreground">
                          Advertising platform that displays relevant ads to help support free access to salary data.
                        </p>
                      </div>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-medium mb-2">Resend</h4>
                        <p className="text-sm text-muted-foreground">
                          Email delivery for free PDF reports and our salary insights newsletter.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Data Coverage & Limitations */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-500" />
                      Data Coverage & Limitations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h3 className="font-semibold mb-3">Geographic Coverage</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            <strong>National Level:</strong> Complete coverage for all 50 states and Washington D.C.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            <strong>State Level:</strong> All states and territories included in BLS OEWS surveys
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            <strong>Metropolitan Areas:</strong> Coverage varies by MSA size and occupation density. Smaller MSAs may have limited data for specialized occupations.
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-3">Data Limitations</h3>
                      <div className="bg-amber-50 dark:bg-amber-950/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                        <h4 className="font-semibold mb-2 text-amber-800 dark:text-amber-200">Suppression Indicators</h4>
                        <p className="text-sm text-amber-700 dark:text-amber-300 mb-2">
                          BLS suppresses data to protect confidentiality or when sample sizes are insufficient. We preserve these indicators:
                        </p>
                        <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1 ml-4">
                          <li><strong>#</strong> - Data not available</li>
                          <li><strong>*</strong> - Data not available or does not meet publication standards</li>
                          <li><strong>**</strong> - Data not available or does not meet publication standards (alternative indicator)</li>
                          <li><strong>-</strong> - Data not available or suppressed</li>
                        </ul>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-3">Historical Data Considerations</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            <strong>Classification Changes:</strong> Industry and occupation classifications have evolved over time (SOC 2010 → 2018, NAICS updates, SIC → NAICS). We use bridge tables to maintain consistency.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            <strong>Methodology Changes:</strong> BLS has updated OEWS methodology over time (e.g., MB3 era changes). We preserve methodology metadata for transparency.
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            <strong>Geographic Boundaries:</strong> Metropolitan area boundaries have changed over time. We use the most current definitions available.
                          </span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* Attribution & Licensing */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Attribution & Licensing
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h3 className="font-semibold mb-3">Government Data Attribution</h3>
                      <p className="text-muted-foreground mb-4">
                        All government data used by Wage Atlas is in the public domain and provided by federal agencies. 
                        We maintain proper attribution to ensure transparency and compliance.
                      </p>
                      <div className="bg-muted/30 p-4 rounded-lg">
                        <p className="text-sm text-muted-foreground mb-2">
                          <strong>BLS Data:</strong> "Data from the U.S. Bureau of Labor Statistics, Occupational Employment and Wage Statistics program."
                        </p>
                        <p className="text-sm text-muted-foreground mb-2">
                          <strong>BEA Data:</strong> "Regional Price Parities data from the U.S. Bureau of Economic Analysis."
                        </p>
                        <p className="text-xs text-muted-foreground">
                          While the underlying government data is public domain, our analysis, visualizations, calculations, 
                          and proprietary insights are protected by copyright. Users may cite Wage Atlas with proper attribution.
                        </p>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-3">Data Usage Rights</h3>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            Government data is public domain and may be used freely with proper attribution
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            Wage Atlas analysis and visualizations may be cited for research and reporting purposes
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-muted-foreground">
                            Bulk data scraping or automated data extraction is prohibited per our Terms of Service
                          </span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* Data Freshness */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <RefreshCw className="h-5 w-5 text-primary" />
                      Data Freshness & Updates
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h3 className="font-semibold mb-3">Update Schedule</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-muted/30 rounded-lg">
                          <h4 className="font-medium mb-2">BLS OEWS Data</h4>
                          <p className="text-sm text-muted-foreground">
                            Updated annually, typically released in the spring for the previous year's data. 
                            We integrate new releases within 30 days of publication.
                          </p>
                        </div>
                        <div className="p-4 bg-muted/30 rounded-lg">
                          <h4 className="font-medium mb-2">BEA RPP Data</h4>
                          <p className="text-sm text-muted-foreground">
                            Updated annually, typically released in the fall. We integrate new releases within 30 days of publication.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-3">Current Data Year</h3>
                      <p className="text-muted-foreground">
                        Our platform currently includes data from <strong>1997 through 2025</strong>, with the most recent 
                        year representing the latest available BLS OEWS release. Historical data is preserved to enable 
                        long-term trend analysis.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Why official sources */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-primary" />
                      Why We Build on Official Sources (and Not Crowdsourced Data)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-muted-foreground">
                    <p>
                      Most salary websites are built on self-reported figures: users type in what they earn,
                      and the site averages the submissions. That approach produces large sample sizes but a
                      systematically skewed sample — people who submit salaries online are younger, better
                      paid, more urban, and more concentrated in tech-adjacent fields than the workforce as a
                      whole. Nobody verifies the numbers, and there is a well-documented tendency to round up.
                    </p>
                    <p>
                      The OEWS survey that powers Wage Atlas works the other way around: a statistically
                      designed sample of about 1.1 million business establishments, with wages reported by
                      employers from payroll records, weighted to represent the full economy, and published
                      with documented error bands. It covers occupations crowdsourced sites barely see —
                      there are far more home health aides than data scientists in America, and only one of
                      those groups posts salaries on the internet.
                    </p>
                    <p>
                      The trade-off is honest: official data is slower (annual releases, three-year survey
                      windows) and excludes equity and bonus compensation. For the fast-moving top of the tech
                      market, crowdsourced data adds color. For everyone else — and for any comparison across
                      occupations, states, or years — the federal survey is the only data with a defensible
                      claim to representing reality. We document its limitations openly on our{' '}
                      <Link to="/methodology" className="text-primary hover:underline">methodology page</Link>{' '}
                      rather than pretending it has none.
                    </p>
                    <div className="p-4 bg-muted/30 rounded-lg">
                      <h4 className="font-medium mb-2 text-foreground">Citing Wage Atlas</h4>
                      <p className="text-sm">
                        Journalists, researchers, and students are welcome to cite our pages. Please attribute
                        the underlying figures to "U.S. Bureau of Labor Statistics, OEWS (via Wage Atlas)" and
                        link to the specific page used. For custom data questions, contact
                        research@wageatlas.com.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Related Resources */}
                <Card>
                  <CardHeader>
                    <CardTitle>Related Resources</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Learn more about how we process and present this data:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Link 
                        to="/methodology" 
                        className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <h4 className="font-medium mb-2">Methodology</h4>
                        <p className="text-sm text-muted-foreground">
                          Learn how we calculate salary data, career trends, and labor market insights.
                        </p>
                      </Link>
                      <Link 
                        to="/what-is-soc-code" 
                        className="p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <h4 className="font-medium mb-2">SOC Code Guide</h4>
                        <p className="text-sm text-muted-foreground">
                          Understand the Standard Occupational Classification system used to organize occupations.
                        </p>
                      </Link>
                    </div>
                  </CardContent>
                </Card>

                {/* Contact */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-primary" />
                      Questions About Our Data?
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      We're committed to transparency about our data sources and processing. If you have questions 
                      about our data, sources, or methodology, please reach out:
                    </p>
                    <div className="space-y-2">
                      <p><strong>Data Questions:</strong> data@wageatlas.com</p>
                      <p><strong>Research Inquiries:</strong> research@wageatlas.com</p>
                      <p><strong>Technical Issues:</strong> support@wageatlas.com</p>
                    </div>
                    <div className="mt-4 p-4 bg-muted/30 rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        <strong>Note:</strong> For questions about the underlying government data itself, please refer to 
                        the <a href="https://www.bls.gov/oes/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">BLS OEWS program</a> or 
                        {' '}<a href="https://www.bea.gov" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">BEA</a> directly.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="hidden lg:block space-y-8">
                <RectangleAd className="sticky top-4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DataSources;

